<template>
  <div class="field" :class="modifiers">
    <label
      v-if="isLabelVisible"
      :for="name"
      class="field__label"
      :class="{ field__label_required: isFieldRequired }"
    >
      {{ label }}
      <slot name="label" />
    </label>
    <div
      class="field__container"
      :class="{ field__container_has_tooltip: Boolean(tooltip) }"
    >
      <input
        v-if="inputPlaceholder"
        class="field__el field__el_placeholder"
        :value="inputPlaceholder"
        disabled
      />
      <input
        v-if="hasType"
        ref="el"
        class="field__el field__el_input"
        :class="`field__el_type_${$attrs.type}`"
        v-bind="attributes"
        v-on="listeners"
      />
      <textarea
        v-else
        ref="el"
        class="field__el field__el_textarea"
        v-bind="attributes"
        v-on="listeners"
      />
      <button
        v-if="isClearVisible"
        type="button"
        class="field__clear"
        tabindex="-1"
        @click="clearValue"
      />
      <component
        v-if="tooltip"
        :is="tooltip.is"
        v-bind="tooltip"
        class="field__tooltip"
      />
      <slot name="container" />
    </div>
    <div class="field__message" v-html="message" />
    <slot />
  </div>
</template>

<script>
import IMask from 'imask'; // https://imask.js.org/guide.html

export default {
  name: 'BaseField',
  inheritAttrs: false,
  props: {
    name: {
      type: String,
      default() {
        const randomId = Math.random().toString(36).substr(2, 9);
        return `field_${randomId}`;
      },
    },
    label: {
      type: String,
      default: '',
    },
    value: {
      type: [String, Number],
      default: '',
    },
    tooltip: {
      type: Object,
      default: null,
    },
    maskOptions: {
      type: Object,
      default: null,
    },
    hasValidStatus: {
      type: Boolean,
      default: true,
    },
    hasClearButton: {
      type: Boolean,
      default: true,
    },
    errorMessage: {
      type: String,
      default: '',
    },
    inputPlaceholder: {
      type: String,
      default: '',
    },
    completeValidator: {
      type: Function,
      default(value, field) {
        if (!value && !field.isFieldRequired) {
          return true;
        }
        if (field.isFieldRequired && !field.imask) {
          return field.hasValue;
        }
        if (field.imask) {
          return field.imask.masked.isComplete;
        }
        return true;
      },
    },
    valueValidator: {
      type: Function,
      default: (value) => value !== '',
    },
  },
  data() {
    return {
      isFieldFocused: false,
      message: '',
    };
  },
  staticData: () => ({
    imask: null,
  }),
  computed: {
    attributes() {
      return {
        value: this.value,
        name: this.name,
        id: this.name,
        ...this.$attrs,
      };
    },
    listeners() {
      return {
        ...this.$listeners,
        focus: this.onFieldFocus,
        blur: this.onFieldBlur,
        input: this.onFieldInput,
      };
    },
    isLabelVisible() {
      return this.label || this.$slots.label;
    },
    isClearVisible() {
      const isDisabled = this.isFieldReadonly || this.isFieldDisabled;
      return !isDisabled && this.hasClearButton && this.hasValue;
    },
    isFieldRequired() {
      return Boolean(this.$attrs.required);
    },
    isFieldReadonly() {
      return Boolean(this.$attrs.readonly);
    },
    isFieldDisabled() {
      return Boolean(this.$attrs.disabled);
    },
    isStatusVisible() {
      return this.hasValue && this.hasValidStatus;
    },
    isFieldValid() {
      return this.completeValidator(this.value, this);
    },
    hasValue() {
      return this.valueValidator(this.value, this);
    },
    hasType() {
      return Boolean(this.$attrs.type);
    },
    modifiers() {
      return {
        field_has_clear: this.isClearVisible,
        field_has_value: this.hasValue,
        field_has_label: this.isLabelVisible,
        field_required: this.isFieldRequired,
        field_readonly: this.isFieldReadonly,
        field_disabled: this.isFieldDisabled,
        field_focused: this.isFieldFocused,
        field_invalid: this.isStatusVisible && !this.isFieldValid,
        field_valid: this.isStatusVisible && this.isFieldValid,
      };
    },
  },
  watch: {
    value: 'updateMaskValue',
  },
  created() {
    this.initFieldMask();
  },
  methods: {
    getName() {
      return this.name;
    },
    focus() {
      return this.$refs.el?.focus();
    },
    blur() {
      return this.$refs.el?.blur();
    },
    onFieldFocus() {
      this.isFieldFocused = true;
      this.$emit('focus');
    },
    onFieldBlur(e) {
      const isClearButton = Boolean(e?.relatedTarget?.classList.contains('field__clear'));
      if (isClearButton) {
        return;
      }
      if (!this.isFieldValid && this.hasValue) {
        this.setErrorMessage();
      }
      this.isFieldFocused = false;
      this.$emit('blur');
    },
    onFieldInput(e) {
      if (!this.imask) {
        this.onValueUpdated(e.target.value);
      }
    },
    onMaskAccepted() {
      if (this.imask && this.imask.value !== this.value) {
        this.onValueUpdated(this.imask.value);
      }
    },
    onValueUpdated(value) {
      this.setMessage('');
      this.$emit('input', value);
    },
    updateMaskValue(value) {
      // При изменении value вне поля нужно обновить маску
      if (this.imask && this.imask.value !== value) {
        this.imask.value = value;
        this.onFieldBlur();
      }
    },
    clearValue() {
      this.onValueUpdated('');
      if (!this.imask) {
        this.$nextTick(this.onFieldBlur);
      }
      return this.focus();
    },
    checkValidity() {
      return this.isFieldValid;
    },
    checkHasValue() {
      return this.hasValue;
    },
    setMessage(message) {
      this.message = message;
    },
    setErrorMessage() {
      this.setMessage(this.errorMessage);
    },
    initFieldMask() {
      if (!this.maskOptions) return;

      // Когда нет DOM, создаем маску без элемента
      // Чтобы проверка в completeValidator cработала правильно при создании
      this.imask = { masked: IMask.createMask(this.maskOptions) };
      this.imask.masked.resolve(this.value);

      // Заменяем маску и привязываем к элементу
      this.$once('hook:mounted', () => {
        this.imask = new IMask(this.$refs.el, this.maskOptions);
        this.imask.on('accept', this.onMaskAccepted);
        if (this.imask.value) {
          this.onValueUpdated(this.imask.value);
          this.onFieldBlur();
        }
      });

      this.$once('hook:beforeDestroy', () => this.imask.destroy?.());
    },
  },
};
</script>

<style lang="stylus">
.field
  display block

  $field_tooltip_width = 4em

  &__label
    display inline-block
    vertical-align top
    font-size 0.85em
    font-weight 400
    letter-spacing 0.01em
    color $c_text_dim
    margin-bottom 0.4em
    cursor pointer
    ~/_disabled &
      pointer-events none

    &_required
      &:after
        content '*'
        color $c_error
        margin-left 0.15em
        ~/_valid &
        ~/_readonly &
        ~/_disabled &
          display none

  &__container
    position relative
    &_has_tooltip
      padding-right $field_tooltip_width

  &__tooltip
    abs top bottom right
    display flex
    align-items center
    justify-content center
    width $field_tooltip_width

  &__message
    display block
    font-size 0.8em
    min-height 1em
    margin-top 0.5em
    ~/_invalid &
      color $c_error

  &__clear
    abs top right
    width 3.4em
    height @width
    padding 1em
    background none
    outline none
    border 0
    z-index 5
    &:after
      content ''
      display block
      width 100%
      height 100%
      background url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath d='M26 26L6 6M26 6L6 26' stroke-linecap='round' stroke='%238B93A6' stroke-width='2'/%3E%3C/svg%3E") no-repeat center
    ~/__container_has_tooltip &
        right $field_tooltip_width

  &__el
    display block
    width 100%
    max-width 100%
    font-family inherit
    font-size 1em
    color $c_text
    font-style normal
    font-weight 400
    text-transform none
    line-height 1
    height 3.4em
    padding 0 1.2em
    background-color #fff
    box-shadow inset 0 0 0 0.07em #DCDEE5
    border-radius 0.2em
    transition box-shadow 200ms ease-in
    appearance none
    outline none
    border 0

    // ~/_valid &
    //   box-shadow inset 0 0 0 0.07em $c_success

    ~/_invalid &
      box-shadow inset 0 0 0 0.1em $c_error

    &:focus
      box-shadow inset 0 0 0 0.1em #96B4FF

    &::placeholder
      font-family inherit
      color $c_text_dim
      font-size inherit
      font-style normal
      font-weight 400
      text-overflow ellipsis
      line-height normal
      opacity 1

    &_textarea
      height auto
      min-height 6em
      padding-top 12px
      padding-bottom @padding-top
      resize vertical
      overflow-x hidden
      overflow-y auto
      line-height 1.2

    &_placeholder
      abs top left
      background @background-color
      color $c_text_dim
      pointer-events none
      ~/__container_has_tooltip &
        width 'calc(100% - %s)' % $field_tooltip_width

    &_placeholder + &
      position relative
      background none
      z-index 1

    &_type_number
      -moz-appearance textfield // Firefox
      &::-webkit-outer-spin-button
      &::-webkit-inner-spin-button
        -webkit-appearance none
        margin 0

    &[disabled]:not(&_placeholder)
      background-color #f2f2f2
      color $c_text_dim
      box-shadow none
      &::placeholder
        opacity 0
</style>
